/**
 * 리다이렉트 착지 화면의 **1회 실행** 스토어. React 비의존(스토어만, 훅은 각 앱에).
 *
 * ── 왜 useMutation 이 아닌가 ────────────────────────────────────────────
 * "진입 자체가 트리거인 비멱등 POST 1회 + 결과 표시" 는 컴포넌트 생명주기와
 * 궁합이 나쁘다. React 19 StrictMode 는 dev 에서 mount→unmount→remount 를 실제로
 * 수행하는데, 그 사이 useMutation observer 가 파기되면 mutate 결과가 화면에
 * 도달하지 못한다(=결제는 됐는데 '승인 중…' 에서 멈춤). 라우트 재평가·부모
 * 리렌더로 인한 리마운트도 같은 결과를 낳는다.
 *
 * 그래서 실행과 결과를 컴포넌트 밖으로 뺀다:
 *   - 실행은 모듈 스코프 promise — 어느 인스턴스가 죽어도 계속 돈다.
 *   - 결과는 이 스토어에 쓰고, 화면은 useSyncExternalStore 로 구독만 한다.
 *   - key(리다이렉트 쿼리에서 뽑은 안정적인 값)로 정확히 1회만 실행한다.
 *
 * key 는 **결제 단위로 유일**해야 한다. 보통 `${orderId}:${paymentKey}` 다 —
 * 리마운트에도 안 변하고, 다른 결제와 절대 겹치지 않는다.
 */

export type RedirectRunPhase = 'pending' | 'success' | 'error';

export interface RedirectRun<T = unknown> {
  phase: RedirectRunPhase;
  /** phase==='success' 일 때의 반환값. */
  data?: T;
  /** phase==='error' 일 때 던져진 값(ApiError 등). */
  error?: unknown;
}

const runs = new Map<string, RedirectRun>();
const listeners = new Map<string, Set<() => void>>();

function emit(key: string, run: RedirectRun): void {
  runs.set(key, run);
  listeners.get(key)?.forEach((l) => l());
}

/** useSyncExternalStore 의 subscribe. 반환값은 해지 함수. */
export function subscribeRedirectRun(key: string, cb: () => void): () => void {
  let set = listeners.get(key);
  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(cb);
  return () => {
    set.delete(cb);
    if (set.size === 0) listeners.delete(key);
  };
}

/**
 * useSyncExternalStore 의 getSnapshot.
 * 저장된 객체 **참조를 그대로** 돌려준다 — 매번 새 객체를 만들면 무한 렌더가 된다.
 */
export function getRedirectRun<T = unknown>(key: string): RedirectRun<T> | null {
  return (runs.get(key) as RedirectRun<T> | undefined) ?? null;
}

/**
 * key 기준 정확히 1회 실행. 이미 시작됐으면(pending/완료) no-op 이라
 * 리마운트마다 호출해도 안전하다 — 그게 이 함수를 effect 에서 부르는 이유다.
 */
export function startRedirectRun<T>(key: string, task: () => Promise<T>): void {
  if (runs.has(key)) return;
  emit(key, { phase: 'pending' });
  task().then(
    (data) => emit(key, { phase: 'success', data }),
    (error: unknown) => emit(key, { phase: 'error', error }),
  );
}

/**
 * 재시도용 초기화. 사용자가 '다시 시도' 를 누른 경우에만 쓴다 —
 * 자동 재실행에 쓰면 1회 실행 보장이 통째로 무너진다.
 */
export function resetRedirectRun(key: string): void {
  runs.delete(key);
  listeners.get(key)?.forEach((l) => l());
}
